import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import Ripple from '@/components/Ripple'
import { useCurrencyList, useTransactionRefresh } from '@/hooks'
import { toFixedNoRound, formatThousands } from '@/utils'
import { Toast, Dialog } from 'react-vant'
import CurrencySelectPopup from '@/components/CurrencySelectPopup'
import { APIWeb } from '@/api'

// 缓存 key
const REMITTANCE_FROM_CURRENCY_KEY = 'remittance_from_currency'
const REMITTANCE_FROM_AMOUNT_KEY = 'remittance_from_amount'

// 币种类型
type CurrencyType = 'USDT' | 'USDC' | 'USD1'

export default React.memo(function Remittance() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { currencyList } = useCurrencyList()
  const { refreshAfterTransaction } = useTransactionRefresh()

  // 获取币种图标
  const getCurrencyIcon = useCallback((currency: string) => {
    const currencyMap: Record<string, string> = {
      'USDT': '/images/home/usdt.png',
      'USDC': '/images/home/usdc.png',
      'USD1': '/images/home/usd1.png',
      'USD': '/images/home/usd.png',
    }
    return currencyMap[currency.toUpperCase()] || '/images/home/usdt.png'
  }, [])

  // 汇出币种 - 使用缓存或默认值
  const [fromCurrency, setFromCurrency] = useState<CurrencyType>(() => {
    const cached = localStorage.getItem(REMITTANCE_FROM_CURRENCY_KEY)
    return (cached as CurrencyType) || 'USDT'
  })
  const [fromAmount, setFromAmount] = useState(() => 
    sessionStorage.getItem(REMITTANCE_FROM_AMOUNT_KEY) || ''
  )

  // 实际汇出金额（手续费等计算后的实际金额）
  const [actualAmount, setActualAmount] = useState('')

  // 收款人
  const [selectedPayee, setSelectedPayee] = useState<ModelsPaymentAccount | null>(null)

  // 提交加载状态
  const [loading, setLoading] = useState(false)

  // VIP 提示对话框
  const [showVipDialog, setShowVipDialog] = useState(false)

  // Popup 控制状态
  const [showCurrencyPopup, setShowCurrencyPopup] = useState(false)
  const [currencySelectType, setCurrencySelectType] = useState<'from' | 'to' | null>(null)

  // 获取当前币种信息
  const fromCurrencyInfo = useMemo(() =>
    currencyList.find(c => c.name === fromCurrency),
    [currencyList, fromCurrency]
  )

  // 实际汇出金额计算（这里可以根据手续费等计算）
  const calculatedActualAmount = useMemo(() => {
    if (!fromAmount) return ''
    // TODO: 根据实际业务逻辑计算手续费等
    // 目前暂时等于汇出金额
    return fromAmount
  }, [fromAmount])

  // 监听币种变化，保存到缓存
  useEffect(() => {
    localStorage.setItem(REMITTANCE_FROM_CURRENCY_KEY, fromCurrency)
  }, [fromCurrency])

  // 同步 fromAmount 到 sessionStorage，跳转收款人页面后可恢复
  useEffect(() => {
    sessionStorage.setItem(REMITTANCE_FROM_AMOUNT_KEY, fromAmount)
  }, [fromAmount])

  // 同步实际汇出金额
  useEffect(() => {
    setActualAmount(calculatedActualAmount)
  }, [calculatedActualAmount])

  // 接收从收款人列表页面传递的数据
  useEffect(() => {
    const state = location.state as { selectedPayee?: ModelsPaymentAccount }
    if (state?.selectedPayee) {
      setSelectedPayee(state.selectedPayee)
      // 清除 location.state，防止重复设置
      window.history.replaceState({}, '')
    }
  }, [location.state])

  // 设置最大金额
  const handleMaxFrom = useCallback(() => {
    if (fromCurrencyInfo) {
      setFromAmount(toFixedNoRound(fromCurrencyInfo.balance, 2))
    }
  }, [fromCurrencyInfo])

  // 移除 handleMaxTo

  // 选择汇出币种
  const handleSelectFromCurrency = useCallback(() => {
    setCurrencySelectType('from')
    setShowCurrencyPopup(true)
  }, [])

  // 处理币种选择
  const handleCurrencySelect = useCallback((currency: { symbol: string }) => {
    const symbol = currency.symbol as CurrencyType
    if (currencySelectType === 'from') {
      setFromCurrency(symbol)
      setFromAmount('')
    }
    setShowCurrencyPopup(false)
    setCurrencySelectType(null)
  }, [currencySelectType])

  // 添加或选择收款人
  const handleAddPayee = useCallback(() => {
    navigate('/remittance/payee-list', {
      state: {
        selectMode: true  // 标识从汇款页面进入，用于返回时的路由判断
      },
      replace: true
    })
  }, [navigate])

  // 查看汇款记录
  const handleViewRecord = useCallback(() => {
    navigate('/remittance/record')
  }, [navigate])

  // 检查表单是否有效
  const isFormValid = useMemo(() => {
    return (
      selectedPayee &&
      fromAmount &&
      parseFloat(fromAmount) > 0
    )
  }, [selectedPayee, fromAmount])

  // 显示 VIP 提示对话框
  const handleShowVipDialog = useCallback(() => {
    setShowVipDialog(true)
  }, [])

  // 关闭 VIP 提示对话框
  const handleCloseVipDialog = useCallback(() => {
    setShowVipDialog(false)
  }, [])

  // 提交汇款
  const handleSubmit = useCallback(async () => {
    if (loading) return

    if (!selectedPayee) {
      Toast.info(t('请选择收款人'))
      return
    }
    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      Toast.info(t('请输入汇出金额'))
      return
    }

    // 显示 VIP 提示对话框
    handleShowVipDialog()
    return

    // 以下是原有的提交逻辑，暂时不删除

    // try {
    //   setLoading(true)

    //   // 构造接口参数
    //   const payTypeMap: Record<CurrencyType, string> = {
    //     'USDT': 'usdt',
    //     'USDC': 'usdc',
    //     'USD1': 'usd1'
    //   }

    //   const params: RemittanceRequest = {
    //     amount: parseFloat(fromAmount),
    //     pay_type: payTypeMap[fromCurrency] as any,
    //     payment_account_id: selectedPayee.id!,
    //     remark: '' // 可以添加备注字段
    //   }

    //   await APIWeb.remittance(params)

    //   Toast.success(t('汇款成功'))
    //   // 交易成功后刷新状态
    //   await refreshAfterTransaction()
    //   // 清空表单
    //   setFromAmount('')
    //   setActualAmount('')
    //   setSelectedPayee(null)
    //   // 返回上一页
    //   navigate(-1)
    // } catch (error) {
    //   console.error('汇款失败:', error)
    //   // Toast 会自动显示错误信息
    // } finally {
    //   setLoading(false)
    // }
  }, [selectedPayee, fromAmount, fromCurrency, loading, navigate, t])

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-3.75 font-medium text-black">{t('汇款')}</span>}
        rightChildren={
          <div className='flex items-center cursor-pointer'>
            <div className='w-6 h-6' onClick={handleViewRecord}>
              <img src="/icon/record-icon.svg" alt="record" />
            </div>
          </div>
        }
      />

      <div className="bg-white px-6 py-6">
        {/* 汇出部分 */}
        <div className="mb-6">
          <div className="text-3.5 text-[#0F0F0F]/60 mb-3">{t('汇出')}</div>

          {/* 币种选择和金额 */}
          <div className="flex min-h-16.5 items-center justify-between bg-white rounded-2xl p-4 border-[0.5px] border-solid border-[#E9E9E9]">
            <div className="flex items-center flex-shrink-0" onClick={handleSelectFromCurrency}>
              <img src={fromCurrencyInfo?.icon} alt={fromCurrency} className="w-5 h-5 mr-3" />
              <div className="flex items-center">
                <span className="text-base font-bold text-black mr-2">{fromCurrency}</span>
                <img src="/icon/picker-down.svg" alt="" className="w-3" />
              </div>
            </div>

            <div className="flex items-center justify-end flex-1 min-w-0">
              <input
                type="number"
                value={fromAmount}
                onChange={(e) => setFromAmount(e.target.value)}
                placeholder="0.00"
                className="text-right text-5 font-semibold text-black placeholder-[#1A1A1A]/60 outline-none flex-1 min-w-0"
              />
              <Ripple className="ml-2 rounded-lg flex-shrink-0">
                <div
                  onClick={handleMaxFrom}
                  className="px-3 py-1 bg-white text-3 rounded-lg border-[0.5px] border-solid border-[#E9E9E9]"
                >
                  {t('最大')}
                </div>
              </Ripple>
            </div>
          </div>

          {/* 可用余额 */}
          <div className="text-3 text-[#979797] mt-2">
            {t('可用余额')}: <span className='text-black ml-2'>{formatThousands(fromCurrencyInfo?.balance || 0, 2, true)} {fromCurrency}</span>
          </div>
        </div>

        {/* 收款人 */}
        <div className="mb-6">
          <div className="text-3.5 text-[#0F0F0F]/60 mb-3">{t('收款人')}</div>

          <Ripple className="rounded-2xl">
            <div
              onClick={handleAddPayee}
              className="flex min-h-16.5 items-center justify-between bg-white border-[0.5px] border-solid border-[#E9E9E9] rounded-2xl p-4"
            >
              {selectedPayee ? (
                // 显示已选择的收款人信息
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-black">{selectedPayee.bank_name}</span>

                    <div className="flex items-center gap-2 text-4 font-medium px-2 py-1 rounded">
                      <img
                        src={getCurrencyIcon(selectedPayee.account_ccy || 'USD')}
                        alt={selectedPayee.account_ccy}
                        className="w-6 h-6"
                      />
                      <span>{selectedPayee.account_ccy}</span>
                    </div>
                  </div>
                  <div className="text-xs text-[#0F0F0F]/60 mb-1">
                    {t('账户名')}: {selectedPayee.account_name}
                  </div>
                  <div className="text-xs text-[#0F0F0F]/60 mb-1">
                    {t('银行账户')}: {selectedPayee.bank_account}
                  </div>
                  <div className="text-xs text-[#0F0F0F]/60 mb-1">
                    {t('银行所在国家或地区')}: {selectedPayee.bank_country}
                  </div>
                  <div className="text-xs text-[#0F0F0F]/60">
                    {t('账户币种')}: {selectedPayee.account_ccy}
                  </div>
                </div>
              ) : (
                // 未选择时显示提示
                <div className="w-full flex justify-between items-center text-[var(--primary-color)]">
                  <div className="flex items-center">
                    <div className='w-6 h-6 bg-[#F4F6F9] rounded-full mr-3 flex items-center justify-center'>
                      <img src="/icon/add-black.svg" alt="" />
                    </div>
                    <span className="text-xs text-black/50 font-bold">{t('添加或选择收款人')}</span>
                  </div>
                  <div className="ml-3">
                    <img src="/icon/address-book.svg" alt="" />
                  </div>
                </div>

              )}

            </div>
          </Ripple>
        </div>

        {/* 实际汇出 */}
        <div className="mb-6">
          <div className="text-3.5 text-[#0F0F0F]/60 mb-3">{t('实际汇出')}</div>

          {/* 显示实际汇出金额（币种跟随汇出币种） */}
          <div className="flex min-h-16.5 items-center justify-between bg-white border-[0.5px] border-solid border-[#E9E9E9] rounded-2xl p-4">
            <div className="flex items-center flex-shrink-0">
              {/* <img src={fromCurrencyInfo?.icon} alt={fromCurrency} className="w-5 h-5 mr-3" />
              <span className="text-base font-bold text-black">{fromCurrency}</span> */}
              <img src='/images/home/usd.png' alt='USD' className="w-5 h-5 mr-3" />
              <span className="text-base font-bold text-black">USD</span>
            </div>

            <div className="flex items-center justify-end flex-1 min-w-0">
              <input
                type="text"
                value={actualAmount}
                readOnly
                placeholder="0.00"
                className="text-right text-5 font-semibold text-black placeholder-[#1A1A1A]/60 outline-none flex-1 min-w-0 bg-transparent"
              />
            </div>
          </div>
        </div>

        {/* 提交按钮 */}
        <div className="mt-8">
          <CustomButton
            onClick={handleSubmit}
            disabled={!isFormValid || loading}
            loading={loading}
          >
            {loading ? t('提交中') : t('提交')}
          </CustomButton>
        </div>
      </div>

      {/* 币种选择 Popup */}
      <CurrencySelectPopup
        visible={showCurrencyPopup}
        onClose={() => {
          setShowCurrencyPopup(false)
          setCurrencySelectType(null)
        }}
        onSelect={handleCurrencySelect}
        currencies={currencyList}
      />

      {/* VIP 提示对话框 */}
      <Dialog
        visible={showVipDialog}
        onClose={handleCloseVipDialog}
        closeable={true}
        showCancelButton={false}
        showConfirmButton={false}
        width="90%"
      >
        <div className="text-center px-6 py-8">
          <div className="text-xl font-500 text-black mb-4 break-all px-5">{t('温馨提示')}</div>
          <div className="text-base text-gray-600 mb-6">
            {t('目前仅向VIP用户开放大额速汇业务')}
          </div>

          {/* 操作按钮 */}
          <div className="flex justify-center items-center">
            <CustomButton
              onClick={handleCloseVipDialog}
              color="primary"
            >
              {t('我知道了')}
            </CustomButton>
          </div>
        </div>
      </Dialog>
    </PageLayout>
  )
})
